import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ItemsList } from './ItemsList';
import { Button } from '@mui/material';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { Content, TDocumentDefinitions } from 'pdfmake/interfaces';

// Load default pdfmake fonts
pdfMake.vfs = pdfFonts.vfs;
pdfMake.fonts = {
  Roboto: {
    normal: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf',
    bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
    italics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
    bolditalics:
      'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf',
  },
};

export interface PlaybookAnalysisData {
  senso: string;
  obiettivi: string;
  ruoli: string;
  regole: string;
  apprendimento: string;
  stakeholder: string;
  summary: string;
  nps: string;
  npsList: string;
  npsTotalPositive: string;
  npsTotalNegative: string;
  totalResponses: string;
  [key: string]: any; // fallback
}

interface PlaybookAnalysisProps {
  analysis: PlaybookAnalysisData | null;
  surveyId: string | null;
}

export const PlaybookAnalysis: React.FC<PlaybookAnalysisProps> = ({ analysis, surveyId }) => {
  const { t } = useTranslation();
  const divRef = useRef<HTMLDivElement>(null);
  const handleDownloadPdf = async () => {
    if (!divRef.current) return;

    // Parse the rendered React HTML to extract styled text
    const elements = divRef.current.querySelectorAll("[class*='MuiTypography']");

    const pdfContent: Content[] = Array.from(elements).map((el) => {
      const text = el.textContent?.trim() || '';
      if (!text) return { text: '' };
      // Detect MUI Typography variant (e.g., h5, h6, body1, body2)
      const variantMatch = Array.from(el.classList)
        .filter((cls) => !cls.includes('MuiTypography-root'))
        .find((cls) => cls.includes('MuiTypography-'));
      const variant = variantMatch ? variantMatch.replace('MuiTypography-', '') : 'body2';

      const content: Content = {
        text,
        style: variant,
      };

      // Add page break before h5s
      if (variant === 'h5' && text !== t('playbook.sintesi')) {
        content.pageBreak = 'before';
      }

      return content;
    });

    // Build the document definition
    const documentDefinition: TDocumentDefinitions = {
      content: pdfContent,
      pageMargins: [40, 40, 40, 40],
      defaultStyle: {
        font: 'Roboto',
        fontSize: 11,
        lineHeight: 1.4,
      },
      styles: {
        h5: { fontSize: 18, bold: true, margin: [0, 0, 0, 20] },
        h6: { fontSize: 15, bold: true, margin: [0, 10, 0, 10] },
        body1: { fontSize: 12, margin: [0, 0, 0, 4] },
        body2: { fontSize: 11, margin: [0, 0, 0, 4] },
      },
    };

    pdfMake.createPdf(documentDefinition).download('playbook' + surveyId + '.pdf');
  };

  const summaryParts: string[] = analysis?.summary
    ?.replace(/(\n)+/g, ' ')
    .split('SECTION: ')
    .filter((p) => p !== '') || ['', '', ''];
  const sensoParts: string[] = analysis?.senso
    ?.replace(/(\n)+/g, ' ')
    .split('SECTION: ')
    .filter((p) => p !== '') || ['', ''];
  const obiettiviParts: string[] = analysis?.obiettivi
    ?.replace(/(\n)+/g, ' ')
    .split('SECTION: ')
    .filter((p) => p !== '') || ['', ''];
  const ruoliParts: string[] = analysis?.ruoli
    ?.replace(/(\n)+/g, ' ')
    .split('SECTION: ')
    .filter((p) => p !== '') || ['', '', ''];
  const regoleParts: string[] = analysis?.regole
    ?.replace(/(\n)+/g, ' ')
    .split('SECTION: ')
    .filter((p) => p !== '') || ['', '', ''];
  const apprendimentoParts: string[] = analysis?.apprendimento
    ?.replace(/(\n)+/g, ' ')
    .split('SECTION: ')
    .filter((p) => p !== '') || ['', '', ''];
  const stakeholderParts: string[] = analysis?.stakeholder
    ?.replace(/(\n)+/g, ' ')
    .split('SECTION: ')
    .filter((p) => p !== '') || ['', '', ''];

  return (
    analysis &&
    (analysis['senso'] === "Non ci sono risposte sufficienti per l'analisi" ? (
      <Typography variant="body2" padding={4}>
        {analysis['senso']}
      </Typography>
    ) : (
      <Box paddingX={4} textAlign="left">
        <div ref={divRef}>
          <Typography variant="h5" paddingBottom={2}>
            {t('playbook.sintesi')}
          </Typography>
          <Typography variant="body2" paddingBottom={2}>
            {summaryParts[0].replace('(sintesi)', '')}
          </Typography>

          <Typography variant="h6" paddingY={2}>
            {t('playbook.punti_di_forza')}
          </Typography>
          <ItemsList stringList={summaryParts[1]} />

          <Typography variant="h6" paddingY={2}>
            {t('playbook.aree_di_miglioramento')}
          </Typography>
          <ItemsList stringList={summaryParts[2]} />

          <Typography variant="h6" paddingY={4}>
            {t('playbook.proposte')}
          </Typography>
          <ItemsList stringList={summaryParts[3]} />

          <Typography variant="h5" paddingY={2}>
            {t('playbook.fondamentali_title')}
          </Typography>
          <Typography variant="body2" paddingBottom={2}>
            {t('playbook.fondamentali_desc')}
          </Typography>

          <Typography variant="h6" paddingY={2}>
            {t('playbook.senso_title')}
          </Typography>
          <Typography variant="body2" paddingBottom={2}>
            {sensoParts[0].replace('(paragrafo)', '')}
          </Typography>
          <Typography variant="body2" paddingBottom={2}>
            <b>{t('playbook.in_sintesi')}</b>: {sensoParts[1].replace('(sintesi)', '')}
          </Typography>

          <Typography variant="h6" paddingY={2}>
            {t('playbook.obiettivi_title')}
          </Typography>
          <Typography variant="body2" paddingBottom={2}>
            {obiettiviParts[0].replace('(paragrafo)', '')}
          </Typography>
          <Typography variant="body1" paddingY={2}>
            {t('playbook.proposte')}
          </Typography>
          <ItemsList stringList={obiettiviParts[1]} />

          <Typography variant="h6" paddingBottom={2}>
            {t('playbook.chiarezza_ruoli_title')}
          </Typography>
          <Typography variant="body2" paddingBottom={2}>
            {ruoliParts[0].replace('(paragrafo)', '')}
          </Typography>
          <Typography variant="body1" paddingY={2}>
            {t('playbook.situazioni_critiche')}
          </Typography>
          <ItemsList stringList={ruoliParts[1]} />
          <Typography variant="body1" paddingY={2}>
            {t('playbook.proposte')}
          </Typography>
          <ItemsList stringList={ruoliParts[2]} />

          <Typography variant="h5" paddingY={2}>
            {t('playbook.norme_collaborative_title')}
          </Typography>
          <Typography variant="body2" paddingBottom={2}>
            {t('playbook.norme_collaborative_desc')}
          </Typography>

          <Typography variant="h6" paddingY={2}>
            {t('playbook.regole_collaborative_title')}
          </Typography>
          <Typography variant="body2" paddingBottom={2}>
            {regoleParts[0].replace('(paragrafo)', '')}
          </Typography>
          <Typography variant="body1" paddingY={2}>
            {t('playbook.temi_importanti')}
          </Typography>
          <ItemsList stringList={regoleParts[1]} />
          <Typography variant="body1" paddingY={2}>
            {t('playbook.proposte')}
          </Typography>
          <ItemsList stringList={regoleParts[2]} />

          <Typography variant="h6" paddingY={2}>
            {t('playbook.apprendimento_title')}
          </Typography>
          <Typography variant="body2" paddingBottom={2}>
            {apprendimentoParts[0].replace('(paragrafo)', '')}
          </Typography>
          <Typography variant="body1" paddingY={2}>
            {t('playbook.temi_importanti')}
          </Typography>
          <ItemsList stringList={apprendimentoParts[1]} />
          <Typography variant="body1" paddingY={2}>
            {t('playbook.proposte')}
          </Typography>
          <ItemsList stringList={apprendimentoParts[2]} />

          <Typography variant="h6" paddingY={2}>
            {t('playbook.engagement_stakeholder_title')}
          </Typography>
          <Typography variant="body2" paddingBottom={2}>
            {stakeholderParts[0].replace('(paragrafo)', '')}
          </Typography>
          <Typography variant="body1" paddingY={2}>
            {t('playbook.temi_importanti')}
          </Typography>
          <ItemsList stringList={stakeholderParts[1]} />
          <Typography variant="body1" paddingY={2}>
            {t('playbook.proposte')}
          </Typography>
          <ItemsList stringList={stakeholderParts[2]} />

          <Typography variant="h5" paddingY={2}>
            {t('playbook.performance_title')}
          </Typography>
          <Typography variant="body2" paddingBottom={2}>
            {t('playbook.performance_desc')}
          </Typography>

          <Typography variant="h6" paddingY={2}>
            {t('playbook.percezione_performance_title')}
          </Typography>
          <Typography variant="body2" paddingBottom={2}>
            {t('playbook.nps_desc_1')}
          </Typography>
          <Typography variant="body2" paddingBottom={2}>
            {t('playbook.nps_question')}
          </Typography>
          <Typography variant="body2" paddingBottom={2}>
            {t('playbook.nps_desc_2')}
          </Typography>
          <Typography variant="body2" paddingBottom={2}>
            {t('playbook.nps_positive')}
          </Typography>
          <Typography variant="body2" paddingBottom={2}>
            {t('playbook.nps_negative')}
          </Typography>
          <Typography variant="body2" paddingBottom={2}>
            {t('playbook.positivi', {
              count: Number(analysis['npsTotalPositive']),
              pct:
                (
                  (Number(analysis['npsTotalPositive']) / Number(analysis['totalResponses'])) *
                  100
                ).toFixed(0) + '%',
            })}
          </Typography>
          <Typography variant="body2" paddingBottom={2}>
            {t('playbook.negativi', {
              count: Number(analysis['npsTotalNegative']),
              pct:
                (
                  (Number(analysis['npsTotalNegative']) / Number(analysis['totalResponses'])) *
                  100
                ).toFixed(0) + '%',
            })}
          </Typography>

          <Typography variant="h6" paddingY={2}>
            {t('playbook.nps_score', { score: analysis['nps'] })}
          </Typography>

          <Typography variant="h6" paddingY={2}>
            {t('playbook.nps_impact_title')}
          </Typography>
          <ItemsList stringList={analysis['npsList']} />
        </div>

        <Button variant="contained" sx={{ mt: 3 }} onClick={handleDownloadPdf}>
          {t('playbook.download_report')}
        </Button>
      </Box>
    ))
  );
};
